import { useEffect, useMemo, useState } from 'react';
import {
	Box, Button, Card, CardContent, CardHeader, Chip, Dialog, DialogActions, DialogContent,
	DialogTitle, FormControl, FormControlLabel, Grid, IconButton, Radio, RadioGroup,
	Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
	Alert, CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rateLimitApi, type RateLimitConfig, type RateLimitHit, type BlacklistEntry, type ActiveLockout } from '@/services/rateLimitApi';

const QK = {
	config: ['rate-limit', 'config'] as const,
	hits: ['rate-limit', 'hits'] as const,
	blacklist: ['rate-limit', 'blacklist'] as const,
	lockouts: ['rate-limit', 'lockouts'] as const,
};

function fmtMs(ms: number): string {
	if (ms < 1000) return ms + ' ms';
	const s = Math.round(ms / 1000);
	if (s < 60) return s + ' giây';
	const m = Math.round(s / 60);
	if (m < 60) return m + ' phút';
	const h = (m / 60).toFixed(1).replace(/\.0$/, '');
	return h + ' giờ';
}
function fmtTs(ts: number): string {
	try {
		return new Date(ts).toLocaleString('vi-VN', { hour12: false });
	} catch { return String(ts); }
}
function reasonChip(r: RateLimitHit['reason']) {
	const color = r === 'blacklisted' ? 'error' : r === 'lockout_active' ? 'warning' : 'info';
	const label = r === 'blacklisted' ? 'Blacklisted' : r === 'lockout_active' ? 'Đang khóa' : 'Vượt giới hạn';
	return <Chip size="small" color={color} label={label} variant="outlined" />;
}

export const RateLimitTab = () => {
	return (
		<Grid container spacing={2.5}>
			<Grid item xs={12} lg={6}><ConfigPanel /></Grid>
			<Grid item xs={12} lg={6}><LockoutsPanel /></Grid>
			<Grid item xs={12}><HistoryPanel /></Grid>
			<Grid item xs={12}><BlacklistPanel /></Grid>
		</Grid>
	);
};

// --- Config ---

const ConfigPanel = () => {
	const qc = useQueryClient();
	const { data, isLoading, error } = useQuery({ queryKey: QK.config, queryFn: rateLimitApi.getConfig });
	const [form, setForm] = useState<RateLimitConfig | null>(null);
	useEffect(() => { if (data) setForm(data); }, [data]);

	const mut = useMutation({
		mutationFn: (body: Partial<RateLimitConfig>) => rateLimitApi.patchConfig(body),
		onSuccess: (next) => { qc.setQueryData(QK.config, next); setForm(next); },
	});

	if (isLoading) return <Card variant="outlined"><CardContent><CircularProgress size={20} /></CardContent></Card>;
	if (error || !form) return <Card variant="outlined"><CardContent><Alert severity="error">Không tải được cấu hình</Alert></CardContent></Card>;

	const handleSave = () => mut.mutate({
		windowMs: form.windowMs,
		limit: form.limit,
		lockoutMs: form.lockoutMs,
		exemptPaths: form.exemptPaths,
	});

	return (
		<Card variant="outlined" sx={{ height: '100%' }}>
			<CardHeader title="Cấu hình rate limit" subheader="Áp dụng tức thì, không cần khởi động lại." />
			<CardContent>
				<Stack spacing={2}>
					<TextField label="Window (ms)" type="number" size="small" value={form.windowMs}
						onChange={(e) => setForm({ ...form, windowMs: Number(e.target.value) })}
						helperText={`= ${fmtMs(form.windowMs)}`} fullWidth />
					<TextField label="Số request tối đa / IP" type="number" size="small" value={form.limit}
						onChange={(e) => setForm({ ...form, limit: Number(e.target.value) })} fullWidth />
					<TextField label="Lockout (ms)" type="number" size="small" value={form.lockoutMs}
						onChange={(e) => setForm({ ...form, lockoutMs: Number(e.target.value) })}
						helperText={`= ${fmtMs(form.lockoutMs)}`} fullWidth />
					<TextField label="Exempt paths (cách nhau dấu phẩy)" size="small"
						value={form.exemptPaths.join(', ')}
						onChange={(e) => setForm({ ...form, exemptPaths: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
						fullWidth />
					{mut.error && <Alert severity="error">Lưu thất bại</Alert>}
					{mut.isSuccess && <Alert severity="success">Đã lưu cấu hình</Alert>}
					<Box>
						<Button variant="contained" disabled={mut.isPending} onClick={handleSave}>
							{mut.isPending ? 'Đang lưu…' : 'Lưu'}
						</Button>
					</Box>
				</Stack>
			</CardContent>
		</Card>
	);
};

// --- Lockouts ---

const LockoutsPanel = () => {
	const qc = useQueryClient();
	const { data = [], isLoading, refetch, isFetching } = useQuery({
		queryKey: QK.lockouts, queryFn: rateLimitApi.getLockouts,
		refetchInterval: 10000,
	});
	const unlock = useMutation({
		mutationFn: (ip: string) => rateLimitApi.unlockIp(ip),
		onSuccess: () => qc.invalidateQueries({ queryKey: QK.lockouts }),
	});

	return (
		<Card variant="outlined" sx={{ height: '100%' }}>
			<CardHeader title="IP đang bị khóa"
				subheader={`${data.length} IP đang trong thời gian lockout`}
				action={<IconButton onClick={() => refetch()} disabled={isFetching}><RefreshIcon /></IconButton>} />
			<CardContent sx={{ pt: 0 }}>
				{isLoading ? <CircularProgress size={20} /> : data.length === 0 ? (
					<Typography variant="body2" color="text.secondary">Không có IP nào đang bị khóa.</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow><TableCell>IP</TableCell><TableCell>Hết hạn</TableCell><TableCell align="right">Hành động</TableCell></TableRow>
						</TableHead>
						<TableBody>
							{data.map((l: ActiveLockout) => (
								<TableRow key={l.ip}>
									<TableCell><code>{l.ip}</code></TableCell>
									<TableCell>{fmtTs(l.expiresAt)}</TableCell>
									<TableCell align="right">
										<Tooltip title="Gỡ khóa ngay">
											<span>
												<Button size="small" startIcon={<LockOpenIcon />}
													disabled={unlock.isPending}
													onClick={() => unlock.mutate(l.ip)}>Unlock</Button>
											</span>
										</Tooltip>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</CardContent>
		</Card>
	);
};

// --- History ---

const HistoryPanel = () => {
	const { data = [], isLoading, refetch, isFetching } = useQuery({
		queryKey: QK.hits, queryFn: () => rateLimitApi.getRecentHits(50),
		refetchInterval: 15000,
	});
	const [ipFilter, setIpFilter] = useState('');
	const [emailFilter, setEmailFilter] = useState('');
	const [dlOpen, setDlOpen] = useState(false);

	const filtered = useMemo(() => {
		const ip = ipFilter.trim().toLowerCase();
		const em = emailFilter.trim().toLowerCase();
		return data.filter(h =>
			(!ip || h.ip.toLowerCase().includes(ip)) &&
			(!em || (h.email ?? '').toLowerCase().includes(em))
		);
	}, [data, ipFilter, emailFilter]);

	return (
		<Card variant="outlined">
			<CardHeader title="Lịch sử chạm rate limit"
				subheader="50 sự kiện mới nhất trong ngày hôm nay. Cần xem cũ hơn → tải file."
				action={
					<Stack direction="row" spacing={1}>
						<IconButton onClick={() => refetch()} disabled={isFetching}><RefreshIcon /></IconButton>
						<Button startIcon={<DownloadIcon />} variant="outlined" size="small" onClick={() => setDlOpen(true)}>
							Tải xuống
						</Button>
					</Stack>
				} />
			<CardContent sx={{ pt: 0 }}>
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
					<TextField size="small" label="Lọc IP" value={ipFilter} onChange={(e) => setIpFilter(e.target.value)} sx={{ flex: 1 }} />
					<TextField size="small" label="Lọc email" value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} sx={{ flex: 1 }} />
				</Stack>
				{isLoading ? <CircularProgress size={20} /> : filtered.length === 0 ? (
					<Typography variant="body2" color="text.secondary">Không có sự kiện phù hợp.</Typography>
				) : (
					<Box sx={{ overflowX: 'auto' }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Thời gian</TableCell>
									<TableCell>IP</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Path</TableCell>
									<TableCell>Lý do</TableCell>
									<TableCell>User agent</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filtered.map((h, i) => (
									<TableRow key={i}>
										<TableCell>{fmtTs(h.ts)}</TableCell>
										<TableCell><code>{h.ip}</code></TableCell>
										<TableCell>{h.email ?? '—'}</TableCell>
										<TableCell><code style={{ fontSize: 12 }}>{h.path}</code></TableCell>
										<TableCell>{reasonChip(h.reason)}</TableCell>
										<TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
											<Tooltip title={h.userAgent ?? ''}><span>{h.userAgent ?? '—'}</span></Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				)}
			</CardContent>
			<DownloadDialog open={dlOpen} onClose={() => setDlOpen(false)} />
		</Card>
	);
};

const DownloadDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
	const today = new Date().toISOString().slice(0, 10);
	const [from, setFrom] = useState(today);
	const [to, setTo] = useState(today);
	const [format, setFormat] = useState<'jsonl' | 'csv'>('jsonl');
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState<string | null>(null);

	const handle = async () => {
		setBusy(true); setErr(null);
		try {
			await rateLimitApi.downloadHits(from, to, format);
			onClose();
		} catch (e) {
			setErr(e instanceof Error ? e.message : 'Tải xuống thất bại');
		} finally { setBusy(false); }
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
			<DialogTitle>Tải xuống lịch sử</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<TextField type="date" label="Từ ngày" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
					<TextField type="date" label="Đến ngày" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
					<FormControl>
						<RadioGroup row value={format} onChange={(e) => setFormat(e.target.value as 'jsonl' | 'csv')}>
							<FormControlLabel value="jsonl" control={<Radio />} label="JSONL (raw)" />
							<FormControlLabel value="csv" control={<Radio />} label="CSV" />
						</RadioGroup>
					</FormControl>
					<Typography variant="caption" color="text.secondary">Tối đa 31 ngày / lần tải.</Typography>
					{err && <Alert severity="error">{err}</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={busy}>Hủy</Button>
				<Button variant="contained" onClick={handle} disabled={busy}>{busy ? 'Đang tải…' : 'Tải xuống'}</Button>
			</DialogActions>
		</Dialog>
	);
};

// --- Blacklist ---

const BlacklistPanel = () => {
	const qc = useQueryClient();
	const { data = [], isLoading, refetch, isFetching } = useQuery({
		queryKey: QK.blacklist, queryFn: rateLimitApi.getBlacklist,
		refetchInterval: 30000,
	});
	const [addOpen, setAddOpen] = useState(false);

	const remove = useMutation({
		mutationFn: (ip: string) => rateLimitApi.removeBlacklist(ip),
		onSuccess: () => qc.invalidateQueries({ queryKey: QK.blacklist }),
	});

	return (
		<Card variant="outlined">
			<CardHeader title="IP Blacklist"
				subheader={`${data.length} IP đang bị chặn`}
				action={
					<Stack direction="row" spacing={1}>
						<IconButton onClick={() => refetch()} disabled={isFetching}><RefreshIcon /></IconButton>
						<Button size="small" variant="contained" onClick={() => setAddOpen(true)}>+ Thêm IP</Button>
					</Stack>
				} />
			<CardContent sx={{ pt: 0 }}>
				{isLoading ? <CircularProgress size={20} /> : data.length === 0 ? (
					<Typography variant="body2" color="text.secondary">Chưa có IP nào.</Typography>
				) : (
					<Box sx={{ overflowX: 'auto' }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>IP</TableCell>
									<TableCell>Lý do</TableCell>
									<TableCell>Tạo lúc</TableCell>
									<TableCell>Hết hạn</TableCell>
									<TableCell>Người tạo</TableCell>
									<TableCell align="right">Hành động</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data.map((b: BlacklistEntry) => (
									<TableRow key={b.ip}>
										<TableCell><code>{b.ip}</code></TableCell>
										<TableCell>{b.reason || '—'}</TableCell>
										<TableCell>{fmtTs(b.createdAt)}</TableCell>
										<TableCell>{b.expiresAt ? fmtTs(b.expiresAt) : 'Vĩnh viễn'}</TableCell>
										<TableCell>{b.createdBy ?? '—'}</TableCell>
										<TableCell align="right">
											<Tooltip title="Gỡ khỏi blacklist">
												<span>
													<IconButton size="small" disabled={remove.isPending}
														onClick={() => remove.mutate(b.ip)}>
														<DeleteOutlineIcon fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				)}
			</CardContent>
			<AddBlacklistDialog open={addOpen} onClose={() => setAddOpen(false)} />
		</Card>
	);
};

const AddBlacklistDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
	const qc = useQueryClient();
	const [ip, setIp] = useState('');
	const [reason, setReason] = useState('');
	const [expiresAt, setExpiresAt] = useState('');
	const add = useMutation({
		mutationFn: () => rateLimitApi.addBlacklist({
			ip: ip.trim(),
			reason: reason.trim() || undefined,
			expiresAt: expiresAt ? new Date(expiresAt).getTime() : null,
		}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: QK.blacklist });
			setIp(''); setReason(''); setExpiresAt('');
			onClose();
		},
	});

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
			<DialogTitle>Thêm IP vào blacklist</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<TextField label="IP" required value={ip} onChange={(e) => setIp(e.target.value)} placeholder="vd: 1.2.3.4" />
					<TextField label="Lý do" value={reason} onChange={(e) => setReason(e.target.value)} multiline minRows={2} />
					<TextField type="datetime-local" label="Hết hạn (để trống = vĩnh viễn)"
						value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} InputLabelProps={{ shrink: true }} />
					{add.error && <Alert severity="error">Thêm thất bại</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Hủy</Button>
				<Button variant="contained" disabled={!ip.trim() || add.isPending}
					onClick={() => add.mutate()}>{add.isPending ? 'Đang thêm…' : 'Thêm'}</Button>
			</DialogActions>
		</Dialog>
	);
};
